import { BigNumber } from "bignumber.js";
import { debounce } from "lodash";
import {
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import { getLedgerBalance } from "src/api/merchant";
import { useChains } from "src/components/FetchConfig";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import useTranslation from "src/hooks/useTranslation";
import {
  amountDivideDecimals,
  displayAmount,
  enumMapping,
  getDecimal,
  toDisplayTime,
} from "src/utils/helper";
import { AlertType } from "src/api/types";
import { BalanceMonitorThresholdFields } from "./types";
import { Button } from "src/components/MuiGenerals";
import { enumTimezone } from "src/utils/constant";
import { useAppSelector } from "src/reducer/hooks";
import { selectProfile } from "src/reducer/profileSlice";
import MpNumberTextField from "src/components/MpTextFieldNumberOnly";
import useAlerting from "src/hooks/useAlerting";

const Enum = {
  timezones: enumMapping(enumTimezone),
};

function BalanceIntervalSection<T extends BalanceMonitorThresholdFields>({
  fields,
  setFields,
  chainName,
  currency,
  clientId,
}: {
  fields: T;
  setFields: Dispatch<SetStateAction<T>>;
  chainName: string;
  currency: string;
  clientId: string;
}) {
  const { timezone } = useAppSelector(selectProfile);
  const { translatePrefix } = usePageConfigProviderContext();
  const { t, tc, te } = useTranslation(translatePrefix);
  const { alerting } = useAlerting();
  const { selectChainByPieces } = useChains();
  const [displayLedgerBalanceObj, setDisplayLedgerBalanceObj] = useState<{
    balance: string;
    displayBalance: string;
    lastUpdateTime: string;
  } | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [invalidClientId, setInvalidClientId] = useState(false);
  const { alertType } = fields;

  const fetchBalance = async () => {
    if (!currency || !chainName || !clientId) {
      setDisplayLedgerBalanceObj(null);
      setInvalidClientId(false);
      return;
    }
    const { chain_type, chain_id } = selectChainByPieces({
      chain_name: chainName,
    });
    const params = {
      client_id: clientId,
      chain_id: chain_id,
      chain_type: chain_type,
      asset_name: currency,
    };
    setIsFetching(true);
    const { result } = await getLedgerBalance(params, {
      isSkipErrorHandling: true,
    });

    const decimals = getDecimal({
      chain_id,
      chain_type,
      asset_name: currency,
    });
    let _balance = "0";

    if (result) {
      setInvalidClientId(false);
      const balance = BigNumber(result.balance);
      const frozenBalance = BigNumber(result.frozen_balance);
      const credit = BigNumber(result.credit);

      const latestBalanceBigNumber = balance
        .minus(frozenBalance)
        .plus(credit)
        .toString();
      _balance = amountDivideDecimals(latestBalanceBigNumber, Number(decimals));

      setDisplayLedgerBalanceObj({
        balance: _balance,
        displayBalance: displayAmount(latestBalanceBigNumber, decimals),
        lastUpdateTime: toDisplayTime(new Date()),
      });
    } else {
      setDisplayLedgerBalanceObj({
        balance: _balance,
        displayBalance: displayAmount(0, decimals),
        lastUpdateTime: toDisplayTime(new Date()),
      });
      alerting("warning", t("client_id_not_found"));
      setInvalidClientId(true);
    }
    setIsFetching(false);
    if (fields.alertType === AlertType.DynamicThreshold) {
      setFields((prev) => ({
        ...prev,
        balance: _balance,
      }));
    }
  };

  // Keep a ref to the latest fetchBalance so the debounced wrapper below
  // always invokes the current closure (it captures fields.alertType etc.).
  const fetchBalanceRef = useRef(fetchBalance);
  fetchBalanceRef.current = fetchBalance;

  const debouncedFetchBalance = useMemo(
    () => debounce(() => fetchBalanceRef.current(), 300),
    [],
  );

  useEffect(() => {
    debouncedFetchBalance();
    return () => debouncedFetchBalance.cancel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, chainName, clientId]);

  useEffect(() => {
    if (
      alertType === AlertType.DynamicThreshold &&
      displayLedgerBalanceObj?.balance
    ) {
      setFields((prev) => ({
        ...prev,
        balance: displayLedgerBalanceObj.balance,
      }));
    }
  }, [alertType]);

  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const interval = e.target.value;
    setFields((prev) => ({
      ...prev,
      interval,
    }));
  };

  return (
    <div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "100px 1fr",
          gap: "8px",
          alignItems: "center",
        }}
      >
        <div style={{ fontSize: "12px", lineHeight: "2rem" }}>
          {t("available_balance")}
        </div>
        {displayLedgerBalanceObj ? (
          <div style={{ fontSize: "12px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <span>
                {displayLedgerBalanceObj.displayBalance} {currency}
              </span>
              <Button
                style={{
                  minWidth: "18px",
                  padding: "4px",
                  borderRadius: "50%",
                }}
                color="secondary"
                variant="text"
                onClick={debouncedFetchBalance}
                disabled={isFetching}
              >
                <RefreshIcon sx={{ width: "0.85em", height: "0.85em" }} />
              </Button>
              <span
                style={{ fontSize: 11, color: "#A2A2A2", lineHeight: "1.3" }}
              >
                {t("update_time", {
                  time: displayLedgerBalanceObj.lastUpdateTime,
                })}{" "}
                GMT{te(Enum.timezones[timezone])}
              </span>
            </div>
            {invalidClientId && (
              <div
                style={{
                  fontSize: 11,
                  color: "#F85858",
                  lineHeight: "1.3",
                }}
              >
                {t("invalid_client_id_warning")}
              </div>
            )}
            <div style={{ fontSize: 11, color: "#A2A2A2", lineHeight: "1.3" }}>
              {t("available_balance_formula")}
            </div>
          </div>
        ) : (
          "--"
        )}
      </div>
      {fields.alertType === AlertType.DynamicThreshold && (
        <>
          <br />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "100px 1fr",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <div style={{ fontSize: "12px", lineHeight: "2rem" }}>
                {t("interval")}
              </div>
              <MpNumberTextField
                sx={{ width: "100%" }}
                label={tc("phInputField", { fieldName: t("interval") })}
                value={fields.interval}
                onChange={handleIntervalChange}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default BalanceIntervalSection;
